import { useCallback, useMemo, useState } from 'react';

import { Tier } from '@backrs_ui/common-ui';

import { CareerPathWithScore, CareerUserResponseSet } from '@backrs_ui/grow-ui/src/models/models';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { RootState } from '@backrs_ui/grow-ui/src/redux/store';
import { getSurveyResults } from '@backrs_ui/grow-ui/src/redux/userResponse/thunks/userResponse.thunk';
import { ScoreCalculationResult } from '@backrs_ui/grow-ui/src/utils/calculateSurveyResults';

import { v4 as uuidv4 } from 'uuid';

type UseExploreTradeoffsResult = Readonly<{
  draftResponseSet: CareerUserResponseSet;
  draftTier: Tier;
  draftCareerPathSuggestions: CareerPathWithScore[];
  draftCareerResults: ScoreCalculationResult;
  isDirty: boolean;
  updateAnswer: (questionId: number, optionIds: number[]) => void;
  markClean: () => void;
}>;

export function useExploreTradeoffs(
  originalResponseSet: CareerUserResponseSet,
): UseExploreTradeoffsResult {
  const careerQuestions = useAppSelector((state: RootState) => state.survey.careerQuestions);
  const careerPaths = useAppSelector((state: RootState) => state.careerPath.careerPaths);
  const careerPathRoles = useAppSelector((state: RootState) => state.careerPath.careerPathRoles);
  const careerPathMeanings = useAppSelector(
    (state: RootState) => state.careerPath.careerPathMeanings,
  );
  const careerPathEnjoyments = useAppSelector(
    (state: RootState) => state.careerPath.careerPathEnjoyments,
  );

  const [draftResponseSet, setDraftResponseSet] =
    useState<CareerUserResponseSet>(originalResponseSet);
  const [baseline, setBaseline] = useState<CareerUserResponseSet>(originalResponseSet);

  const markClean = useCallback(() => {
    setBaseline(draftResponseSet);
  }, [draftResponseSet]);

  const updateAnswer = useCallback((questionId: number, optionIds: number[]) => {
    setDraftResponseSet((prev) => {
      const existingIndex = prev.user_responses.findIndex(
        (r) => r.career_question_id === questionId,
      );

      if (existingIndex !== -1) {
        // Update existing response
        const updatedResponses = prev.user_responses.map((response, i) =>
          i === existingIndex ? { ...response, career_question_option_ids: optionIds } : response,
        );

        return { ...prev, user_responses: updatedResponses };
      }

      // No existing response (question was skipped via conditional logic) — create one
      const newResponse = {
        id: uuidv4(),
        career_user_response_set_id: prev.id,
        career_question_id: questionId,
        career_question_option_ids: optionIds,
      };

      return { ...prev, user_responses: [...prev.user_responses, newResponse] };
    });
  }, []);

  const isDirty = useMemo(() => {
    if (draftResponseSet.user_responses.length !== baseline.user_responses.length) {
      return true;
    }

    return draftResponseSet.user_responses.some((draftAnswer) => {
      const baselineAnswer = baseline.user_responses.find(
        (orig) => orig.career_question_id === draftAnswer.career_question_id,
      );

      if (!baselineAnswer) return true;

      if (
        draftAnswer.career_question_option_ids.length !==
        baselineAnswer.career_question_option_ids.length
      ) {
        return true;
      }

      const sortedDraft = [...draftAnswer.career_question_option_ids].sort();
      const sortedBaseline = [...baselineAnswer.career_question_option_ids].sort();

      return sortedDraft.some((id, i) => id !== sortedBaseline[i]);
    });
  }, [draftResponseSet.user_responses, baseline]);

  const {
    tier: draftTier,
    careerPathSuggestions: draftCareerPathSuggestions,
    careerResults: draftCareerResults,
  } = useMemo(
    () =>
      getSurveyResults({
        userResponseSet: draftResponseSet,
        careerQuestions,
        careerPaths,
        careerPathRoles,
        careerPathMeanings,
        careerPathEnjoyments,
      }),
    [
      draftResponseSet,
      careerQuestions,
      careerPaths,
      careerPathRoles,
      careerPathMeanings,
      careerPathEnjoyments,
    ],
  );

  return {
    draftResponseSet,
    draftTier,
    draftCareerPathSuggestions,
    draftCareerResults,
    isDirty,
    updateAnswer,
    markClean,
  };
}
